import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAuditRecords } from "../../api/admin-governance";
import type { AuditRecord } from "../../api/admin-governance";
import {
  Pagination,
  Td,
  Th,
  fmtDate,
  shortId,
  tableStyles,
} from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";

const PER_PAGE = 25;

interface Filters {
  action: string;
  actor_id: string;
  target_id: string;
  start_date: string;
  end_date: string;
}

const EMPTY_FILTERS: Filters = {
  action: "",
  actor_id: "",
  target_id: "",
  start_date: "",
  end_date: "",
};

export function AuditPage(): React.JSX.Element {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (p: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAuditRecords({
        ...stripEmpty(filtersRef.current),
        page: p,
        per_page: PER_PAGE,
      });
      setRecords(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page);
  }, [page, load]);

  const handleApply = useCallback((): void => {
    setPage(1);
    void load(1);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div>
      <PageHeader
        title={t("audit.title")}
        description={t("audit.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar
        filters={filters}
        onChange={setFilters}
        onApply={handleApply}
      />
      {error && <p style={s.error}>{error}</p>}
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("audit.col_action")}</Th>
            <Th>{t("audit.col_actor")}</Th>
            <Th>{t("audit.col_target")}</Th>
            <Th>{t("audit.col_metadata")}</Th>
            <Th>{t("audit.col_date")}</Th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id}>
              <Td>
                <span style={s.actionBadge}>{r.action}</span>
              </Td>
              <Td mono>{shortId(r.actor_id)}</Td>
              <Td mono>{r.target_id ? shortId(r.target_id) : "-"}</Td>
              <Td>{extractReason(r.metadata)}</Td>
              <Td>{fmtDate(r.created_at)}</Td>
            </tr>
          ))}
          {!loading && records.length === 0 && (
            <tr>
              <Td colSpan={5}>{t("audit.no_records_found")}</Td>
            </tr>
          )}
        </tbody>
      </table>
      {loading && <p style={s.loadingText}>{t("common.loading")}</p>}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

/* ---------- Sub-components ---------- */

function FilterBar({
  filters,
  onChange,
  onApply,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  onApply: () => void;
}): React.JSX.Element {
  const set = (key: keyof Filters, val: string): void => {
    onChange({ ...filters, [key]: val });
  };

  return (
    <div style={s.filterRow}>
      <input
        placeholder={t("audit.placeholder_action")}
        value={filters.action}
        onChange={(e) => set("action", e.target.value)}
        style={s.filterInput}
      />
      <input
        placeholder={t("audit.placeholder_actor_id")}
        value={filters.actor_id}
        onChange={(e) => set("actor_id", e.target.value)}
        style={s.filterInput}
      />
      <input
        placeholder={t("audit.placeholder_target_id")}
        value={filters.target_id}
        onChange={(e) => set("target_id", e.target.value)}
        style={s.filterInput}
      />
      <input
        type="date"
        title={t("audit.start_date")}
        value={filters.start_date}
        onChange={(e) => set("start_date", e.target.value)}
        style={s.filterInput}
      />
      <input
        type="date"
        title={t("audit.end_date")}
        value={filters.end_date}
        onChange={(e) => set("end_date", e.target.value)}
        style={s.filterInput}
      />
      <button type="button" onClick={onApply} style={s.filterBtn}>
        Apply
      </button>
    </div>
  );
}

/* ---------- Helpers ---------- */

function stripEmpty(obj: Filters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v) out[k] = v;
  }
  return out;
}

function extractReason(metadata: Record<string, unknown> | null): string {
  if (!metadata) return "-";
  const reason = metadata.reason;
  if (typeof reason === "string") return truncate(reason, 60);
  const keys = Object.keys(metadata);
  if (keys.length === 0) return "-";
  return truncate(keys.join(", "), 60);
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

/* ---------- Styles ---------- */

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  actionBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    background: "var(--color-bg-secondary)",
    fontFamily: "var(--font-mono)",
  },
};
